import { parseSearchWith } from '@tanstack/react-router';
import * as qs from 'qs-esm';
import { setup, assign, fromPromise, type DoneStateEvent } from 'xstate';
import {
  type DatasetOption,
  DefaultFilterValues,
  type AppProps,
  type HierarchyOptions,
  type Compare,
} from '#src/types';
import { buildTopEntriesParams } from '#src/utils/get-limit';
import { ApiTranslations } from '#src/utils/i18n/api-translations';
import {
  sanitizeCompareBy,
  updateCompareBy,
} from '#src/utils/update-compare-by';
import {
  parseUrlToContext,
  updateUrlDebounced,
} from '#src/utils/url-sync-xstate';
import {
  checkComparison,
  hasThreeDimensions,
  hasTwoDimensions,
  isAreaChart,
  isBarChart,
  isLineChart,
} from './guards';
import { getMultipleYearsLimit } from './selectors';
import {
  type MachineEventsType,
  type MachineContextType,
  type FetchDataOutput,
} from './types';
import {
  updateYearsStepper,
  withYearsStepperDefinition,
} from './years-stepper-utils';

export const initialContext: MachineContextType = {
  dataset: null,
  contextualDataset: null,
  hasInteracted: false,
  settings: {
    view_type: {
      selectedValues: [],
    },
    allowed_view_types: {
      selectedValues: [],
    },
    dataset: {
      showFilter: true,
      selectedValues: [],
    },
    categoryValue: {
      showFilter: true,
      selectedValues: [],
    },
    levelValue: {
      showFilter: true,
      selectedValues: [],
    },
    levelId: {
      showFilter: true,
      selectedValues: [],
    },
    categoryId: {
      showFilter: true,
      selectedValues: [],
    },
    defaultQuery: {
      showFilter: true,
      selectedValues: [],
    },
  },

  yearsMode: 'single',
  selectedYears: [],

  level: DefaultFilterValues.level,
  category: DefaultFilterValues.category,
  fundingSource: DefaultFilterValues.fundingSource,

  compareBy: [],
  levelCompare: [],
  categoryCompare: [],
  fundingSourceCompare: [],

  viewType: 'map',
  chartType: 'bar',
  tableDataView: 'absolute',

  data: {
    datasetData: null,
    datasetOptions: [],
    contextualDatasets: [],
    levelOptions: null,
    categoryOptions: null,
    fundingSources: null,
    yearsOptions: [],
    yearsStepper: null,
    contextualData: null,
    isMonthly: false,
    summary: null,
    mapChartData: null,
    geoData: null,
  },
};

function applySettingsToContext(
  initContext: MachineContextType,
  overrideContext = {} as Partial<MachineContextType>
) {
  let level = initContext.level;
  let levelCompare = initContext.levelCompare;
  let category = initContext.category;
  let categoryCompare = initContext.categoryCompare;
  let dataset = initContext.dataset;
  let viewType = initContext.viewType;

  if (initContext.settings) {
    if (initContext.settings.levelId.selectedValues.length > 0) {
      level = initContext.settings.levelId.selectedValues[0];
      if (initContext.settings.levelValue.selectedValues.length > 0) {
        levelCompare = [initContext.settings.levelValue.selectedValues];
      }
    }
    if (initContext.settings.categoryId.selectedValues.length > 0) {
      category = initContext.settings.categoryId.selectedValues[0];
      if (initContext.settings.categoryValue.selectedValues.length > 0) {
        categoryCompare = [initContext.settings.categoryValue.selectedValues];
      }
    }

    if (initContext.settings.dataset.selectedValues.length > 0) {
      dataset = initContext.settings.dataset.selectedValues[0];
    }
    if (initContext.settings.view_type.selectedValues.length > 0) {
      viewType = initContext.settings.view_type.selectedValues[0];
    } else if (
      initContext.settings.allowed_view_types.selectedValues.length > 0 &&
      !initContext.settings.allowed_view_types.selectedValues.includes(
        initContext.viewType
      )
    ) {
      viewType = initContext.settings.allowed_view_types.selectedValues[0];
    }
  }

  return {
    ...initContext,
    level,
    levelCompare,
    category,
    categoryCompare,
    dataset,
    viewType,
    ...overrideContext,
  };
}

export const machine = setup({
  types: {
    context: {} as MachineContextType,
    events: {} as MachineEventsType,
    input: {} as AppProps,
  },
  guards: {
    shouldSyncUrl: ({ context }) => context.hasInteracted,
    // view types
    isChartView: ({ context }) => context.viewType === 'chart',
    isMapView: ({ context }) => context.viewType === 'map',
    isTableView: ({ context }) => context.viewType === 'table',

    // chart view
    isBar: ({ context }) => {
      return isBarChart(context);
    },
    isVerticalBar: ({ context }) => {
      return context.yearsMode !== 'single' && isBarChart(context);
    },
    isLine: ({ context }) => {
      return isLineChart(context);
    },
    isArea: ({ context }) => {
      return isAreaChart(context);
    },
    isHorizontalStackedBar: ({ context }) => {
      return (
        context.yearsMode === 'single' &&
        (checkComparison(context) || context.category === 'EMK')
      );
    },
    isVerticalStackedBar: ({ context }) => {
      return (
        hasTwoDimensions(context, context.chartType) &&
        isBarChart(context) &&
        context.yearsMode !== 'single'
      );
    },
    isMultipleLine: ({ context }) => {
      return (
        hasTwoDimensions(context, context.chartType) && isLineChart(context)
      );
    },
    isStackedArea: ({ context }) => {
      return (
        hasTwoDimensions(context, context.chartType) && isAreaChart(context)
      );
    },
    isFacetedStackedBar: ({ context }) => {
      return isBarChart(context) && hasThreeDimensions(context);
    },
    isFacetedMultipleLine: ({ context }) => {
      return isLineChart(context) && hasThreeDimensions(context);
    },
    isFacetedStackedArea: ({ context }) => {
      const { yearsMode } = context;
      return (
        isAreaChart(context) &&
        ((yearsMode === 'multiple' && checkComparison(context, 'some')) ||
          checkComparison(context))
      );
    },
    isRowFacetedStackedArea: ({ context }) => {
      const { yearsMode } = context;
      return (
        isAreaChart(context) &&
        yearsMode === 'multiple' &&
        checkComparison(context)
      );
    },
    isNotLastYear: ({ context }) => {
      const currentYear = context.selectedYears[0];
      return (
        context.data.yearsOptions.filter((y) => y > currentYear).length > 0
      );
    },
    hasContextualDataset: ({ context }) => {
      return context.contextualDataset != null;
    },
    // table view
    isThreeDimensionsTable: ({ context }) => {
      return hasThreeDimensions(context);
    },
    isTwoDimensionsTable: ({ context }) => {
      return hasTwoDimensions(context, context.chartType);
    },
  },
  actions: {
    saveDatasets: assign(({ context, event }) => {
      const ev = event as DoneStateEvent<{
        datasets: Omit<DatasetOption, 'name' | 'shortName'>[];
      }>;
      const datasets = ev.output.datasets.filter(
        (d) =>
          context.settings.dataset.selectedValues.includes(d.id) ||
          context.settings.dataset.selectedValues.length === 0
      );

      return {
        ...context,
        dataset: context.dataset ?? datasets[0].id,
        data: {
          ...context.data,
          datasetOptions: datasets.map((d) => {
            const vocabularyItem = ApiTranslations.getMetadataVocabularyItem(
              d.id
            );
            const unitVocabularyItem =
              ApiTranslations.getMetadataVocabularyItem(d.unit);

            if (!vocabularyItem) {
              return {
                ...d,
                name: d.id,
                shortName: d.id,
                unit: d.unit,
                unitName: unitVocabularyItem?.name ?? d.unit,
              };
            }

            return {
              ...d,
              unit: d.unit,
              unitName: unitVocabularyItem?.name ?? d.unit,
              name: vocabularyItem.name,
              shortName: vocabularyItem.shortName,
            };
          }),
        },
      };
    }),
    saveOptions: assign(({ context, event }) => {
      const ev = event as DoneStateEvent<{
        levels: HierarchyOptions;
        categories: HierarchyOptions;
        fundingSources: HierarchyOptions | null;
        years: number[];
        isMonthly: boolean;
        contextualDatasets: { id: string }[];
      }>;
      const { years, contextualDatasets, isMonthly, fundingSources } =
        ev.output;

      const levelEntries = Object.entries(ev.output.levels).filter(
        ([id]) =>
          context.settings.levelId.selectedValues.includes(id) ||
          context.settings.levelId.selectedValues.length === 0
      );
      const levels = levelEntries.reduce((acc, [id, level]) => {
        acc[id] = level;
        return acc;
      }, {} as HierarchyOptions);

      const categoryEntries = Object.entries(ev.output.categories).filter(
        ([id]) =>
          context.settings.categoryId.selectedValues.includes(id) ||
          context.settings.categoryId.selectedValues.length === 0
      );
      const categories = categoryEntries.reduce((acc, [id, category]) => {
        acc[id] = category;
        return acc;
      }, {} as HierarchyOptions);

      let latestYear = years[years.length - 1];

      const yearsAreValid =
        context.selectedYears.length > 0 &&
        context.selectedYears.every((year) => years.includes(year));

      return {
        ...context,
        selectedYears: yearsAreValid ? context.selectedYears : [latestYear],
        data: {
          ...context.data,
          isMonthly,
          fundingSources: fundingSources
            ? ApiTranslations.getHierarchyTexts(fundingSources, 'fundingSource')
            : null,
          levelOptions: ApiTranslations.getHierarchyTexts(levels, 'level'),
          categoryOptions: ApiTranslations.getHierarchyTexts(
            categories,
            'category'
          ),
          yearsOptions: years,
          contextualDatasets: contextualDatasets.map((d) => {
            const vocabularyItem = ApiTranslations.getMetadataVocabularyItem(
              d.id
            );

            if (!vocabularyItem) {
              return {
                ...d,
                name: d.id,
                shortName: d.id,
              };
            }

            return {
              ...d,
              name: vocabularyItem.name,
              shortName: vocabularyItem.shortName,
            };
          }),
        },
      };
    }),
    saveData: assign(({ context, event }) => {
      const ev = event as DoneStateEvent<FetchDataOutput>;
      const [{ data }, { summary }, { data: _mapChartData }, { geoData }] =
        ev.output;

      const datasetData = data.map((d) => ({
        ...d,
        date: new Date(d.date),
      }));

      let mapChartData = null;
      if (_mapChartData) {
        mapChartData = _mapChartData.map((d) => ({
          ...d,
          date: new Date(d.date),
        }));
      }

      return {
        ...context,
        data: {
          ...context.data,
          datasetData,
          summary,
          mapChartData,
          geoData,
        },
      };
    }),
    saveContextualData: assign(({ context, event }) => {
      const ev = event as DoneStateEvent<FetchDataOutput>;
      const [{ data }] = ev.output;

      const contextualData = data.map((d) => ({
        ...d,
        date: new Date(d.date),
      }));

      return {
        ...context,
        data: {
          ...context.data,
          contextualData,
        },
      };
    }),
    saveYearsStepperData: assign(({ context, event }) => {
      const ev = event as DoneStateEvent<FetchDataOutput>;
      const [{ data }, { summary }, { data: _mapChartData }, { geoData }] =
        ev.output;

      const datasetData = data.map((d) => ({
        ...d,
        date: new Date(d.date),
      }));

      let mapChartData = null;
      if (_mapChartData) {
        mapChartData = _mapChartData.map((d) => ({
          ...d,
          date: new Date(d.date),
        }));
      }

      const isLastYear =
        context.data.yearsOptions.filter((y) => y > context.selectedYears[0])
          .length === 0;

      const targetYear = isLastYear
        ? context.data.yearsOptions[0]
        : context.selectedYears[0];

      const newContext: MachineContextType = {
        ...context,
        data: {
          ...context.data,
          yearsStepper: [datasetData, summary, mapChartData, geoData],
        },
      };

      const {
        nextYearDatasetData,
        nextYearSummary,
        nextYearMapChartData,
        nextYearGeoData,
        upcomingYearsDatasetData,
        upcomingYearsSummary,
        upcomingYearsMapChartData,
        upcomingYearsGeoData,
      } = updateYearsStepper(newContext, targetYear);

      return {
        ...newContext,
        selectedYears: [targetYear],
        data: {
          ...newContext.data,
          datasetData: nextYearDatasetData,
          summary: nextYearSummary,
          mapChartData: nextYearMapChartData,
          geoData: nextYearGeoData,
          yearsStepper: [
            upcomingYearsDatasetData,
            upcomingYearsSummary,
            upcomingYearsMapChartData,
            upcomingYearsGeoData,
          ] as MachineContextType['data']['yearsStepper'],
        },
      };
    }),
    updateYearsStepper: assign(({ context }) => {
      if (context.data.yearsStepper == null) {
        return context;
      }

      const targetYear = context.selectedYears[0] + 1;
      const {
        nextYearDatasetData,
        nextYearSummary,
        nextYearMapChartData,
        nextYearGeoData,
        upcomingYearsDatasetData,
        upcomingYearsSummary,
        upcomingYearsMapChartData,
        upcomingYearsGeoData,
      } = updateYearsStepper(context, targetYear);

      return {
        ...context,
        selectedYears: [targetYear],
        data: {
          ...context.data,
          datasetData: nextYearDatasetData,
          summary: nextYearSummary,
          mapChartData: nextYearMapChartData,
          geoData: nextYearGeoData,
          yearsStepper: [
            upcomingYearsDatasetData,
            upcomingYearsSummary,
            upcomingYearsMapChartData,
            upcomingYearsGeoData,
          ] as MachineContextType['data']['yearsStepper'],
        },
      };
    }),
    updateUrl: ({ context }: { context: MachineContextType }) => {
      updateUrlDebounced(context);
    },
    markInteracted: assign({ hasInteracted: true }),
    limitYears: assign(({ context }) => {
      const limit = getMultipleYearsLimit({ context });
      return {
        ...context,
        selectedYears: context.selectedYears.slice(0, limit),
      };
    }),
  },
  actors: {
    fetchDatasets: fromPromise<any, void>(() => {
      return fetch(`${process.env.API_BASE_URL}/api/datasets`).then(
        (response) => {
          if (response.ok) {
            return response.json();
          } else {
            throw new Error('Failed to fetch datasets');
          }
        }
      );
    }),
    fetchOptions: fromPromise<any, { dataset: string }>(
      ({ input: { dataset } }) => {
        return fetch(`${process.env.API_BASE_URL}/api/${dataset}/options`).then(
          (response) => {
            if (response.ok) {
              return response.json();
            } else {
              throw new Error('Failed to fetch options');
            }
          }
        );
      }
    ),
    fetchData: fromPromise<
      any,
      {
        context: Omit<MachineContextType, 'data'>;
        dataset: string;
      }
    >(({ input: { context, dataset } }) => {
      const params: Record<string, any> = {
        dataset,
        level: context.level,
        yearsMode: context.yearsMode === 'range' ? 'range' : 'multiple',
        selectedYears: context.selectedYears,
        category: context.category,
        fundingSource: context.fundingSource,
        ...buildTopEntriesParams(context),
      };
      if (context.levelCompare.length > 0) {
        params.levelCompare = context.levelCompare;
      }
      if (context.categoryCompare.length > 0) {
        params.categoryCompare = context.categoryCompare;
      }
      if (context.fundingSourceCompare.length > 0) {
        params.fundingSourceCompare = context.fundingSourceCompare;
      }
      const query = qs.stringify(params, {
        addQueryPrefix: false,
      });

      const fetchMapData = () => {
        if (context.viewType !== 'map') {
          return [
            Promise.resolve({ summary: null }),
            Promise.resolve({ data: [] }),
            Promise.resolve({ geoData: null }),
          ];
        }
        const summaryQuery = qs.stringify(
          {
            selectedYears: context.selectedYears,
          },
          {
            addQueryPrefix: false,
          }
        );
        const mapSidebarChartQuery = qs.stringify(
          {
            ...params,
            ...buildTopEntriesParams({
              ...context,
              viewType: 'chart',
            }),
          },
          {
            addQueryPrefix: false,
          }
        );
        const mapGeoDataQuery = qs.stringify(
          {
            geoLevelId: context.level,
            selectedYears: context.selectedYears,
          },
          {
            addQueryPrefix: false,
          }
        );

        return [
          fetch(`${process.env.API_BASE_URL}/api/summary?${summaryQuery}`).then(
            (response) => {
              if (response.ok) {
                return response.json();
              } else {
                throw new Error('Failed to fetch summary');
              }
            }
          ),
          fetch(
            `${process.env.API_BASE_URL}/api/data?${mapSidebarChartQuery}`
          ).then((response) => {
            if (response.ok) {
              return response.json();
            } else {
              throw new Error('Failed to fetch data');
            }
          }),
          fetch(`${process.env.API_BASE_URL}/api/geo?${mapGeoDataQuery}`).then(
            (response) => {
              if (response.ok) {
                return response.json();
              } else {
                throw new Error('Failed to fetch geo data');
              }
            }
          ),
        ];
      };

      return Promise.all([
        fetch(`${process.env.API_BASE_URL}/api/data?${query}`).then(
          (response) => {
            if (response.ok) {
              return response.json();
            } else {
              throw new Error('Failed to fetch data');
            }
          }
        ),
        ...fetchMapData(),
      ]);
    }),
  },
}).createMachine({
  id: 'gfx',
  initial: 'fetchingDatasets',
  context: ({ input }) => {
    const initContext = {
      ...initialContext,
      settings: input?.settings ?? initialContext.settings,
    };

    let defaultCtx = {};
    const defaultQuery = initContext.settings.defaultQuery.selectedValues[0];
    if (defaultQuery) {
      const parser = parseSearchWith(JSON.parse);
      const ctx = parser(defaultQuery);
      defaultCtx = parseUrlToContext(ctx);
    }

    const overrideContext = {
      ...defaultCtx,
      ...input?.urlContext,
    };

    const contextWithSettings = applySettingsToContext(
      initContext,
      overrideContext
    );

    return {
      ...contextWithSettings,
      // only start syncing filters to the URL if the page was loaded with
      // an existing querystring (e.g. a shared link); a fresh, param-less
      // load should stay clean until the user actually changes a filter
      hasInteracted: Boolean(
        input?.urlContext && Object.keys(input.urlContext).length > 0
      ),
      compareBy: sanitizeCompareBy(
        contextWithSettings.compareBy,
        contextWithSettings.category ?? DefaultFilterValues.category,
        contextWithSettings.viewType
      ),
    };
  },
  always: {
    guard: 'shouldSyncUrl',
    actions: ['updateUrl'],
  },
  on: {
    CHANGE_DATASET: {
      target: '.fetchingOptions',
      actions: [
        assign(({ event, context }) => {
          const initContext = {
            ...initialContext,
            settings: context.settings,
            selectedYears: context.selectedYears,
            dataset: context.dataset,
            viewType: context.viewType,
            data: {
              ...initialContext.data,
              datasetOptions: context.data.datasetOptions,
            },
          };

          const contextWithSettings = applySettingsToContext(initContext, {
            dataset: event.dataset,
          });

          return contextWithSettings;
        }),
        'markInteracted',
      ],
    },
    CHANGE_YEARS_MODE: {
      target: '.fetchingData',
      actions: [
        assign({
          yearsMode: ({ event }) => event.yearsMode,
          selectedYears: ({ event }) => event.selectedYears,
          chartType: ({ event, context }) => {
            if (isAreaChart(context)) {
              return 'area';
            }
            return event.yearsMode === 'range' ? context.chartType : 'bar';
          },
        }),
        'markInteracted',
      ],
    },
    CHANGE_LEVEL: {
      target: '.fetchingData',
      actions: [
        assign(({ context, event }) => {
          const { level, compare } = event;
          return updateCompareBy('level', level, compare, context);
        }),
        'limitYears',
        'markInteracted',
      ],
    },
    CHANGE_CATEGORY: {
      target: '.fetchingData',
      actions: [
        assign(({ context, event }) => {
          const { category, compare } = event;
          return updateCompareBy('category', category, compare, context);
        }),
        'limitYears',
        'markInteracted',
      ],
    },
    CHANGE_FUNDING_SOURCE: {
      target: '.fetchingData',
      actions: [
        assign(({ context, event }) => {
          return updateCompareBy(
            'fundingSource',
            event.parentId,
            event.compare,
            context
          );
        }),
        'limitYears',
        'markInteracted',
      ],
    },
    CHANGE_VIEW: {
      target: '.fetchingData',
      actions: [
        assign(({ event, context }) => {
          let levelCompare = context.levelCompare;
          let categoryCompare = context.categoryCompare;
          let fundingSourceCompare = context.fundingSourceCompare;
          let chartType = context.chartType;
          let yearsMode = context.yearsMode;
          let selectedYears = context.selectedYears;
          let contextualDataset = context.contextualDataset;
          let contextualData = context.data.contextualData;

          // preserve current category & compareBy so we can override when needed
          let category = context.category;
          let fundingSource = context.fundingSource;
          let compareBy = context.compareBy;

          if (event.viewType === 'map') {
            levelCompare = levelCompare.map((group) => group.slice(0, 1));
            // clear category selections when switching to map so the view respects limits
            if (context.categoryCompare.length !== 1) {
              category = DefaultFilterValues.category;
              categoryCompare = [];
            }

            // clear funding source selections when switching to map so the view respects limits
            if (context.fundingSourceCompare.length !== 1) {
              fundingSource = DefaultFilterValues.fundingSource;
              fundingSourceCompare = [];
            }

            // ensure compareBy no longer includes category nor funding source as a dimension
            compareBy = compareBy.filter((c) => c === 'level');
            fundingSourceCompare = fundingSourceCompare.map((group) =>
              group.slice(0, 1)
            );
            chartType = 'bar';
            yearsMode = 'single';
            selectedYears = context.selectedYears.slice(0, 1);
            contextualDataset = initialContext.contextualDataset;
            contextualData = initialContext.data.contextualData;
          }

          return {
            viewType: event.viewType,
            yearsMode,
            selectedYears,
            chartType,
            levelCompare,
            category,
            fundingSource,
            categoryCompare,
            compareBy,
            fundingSourceCompare,
            contextualDataset,
            data: {
              ...context.data,
              contextualData,
            },
          };
        }),
        'markInteracted',
      ],
    },
    CHANGE_CHART_TYPE: [
      {
        target: '.check',
        actions: [
          assign({
            chartType: ({ event }) => event.chartType,
          }),
          'markInteracted',
        ],
      },
    ],
    CHANGE_CONTEXTUAL_DATASET: [
      {
        target: '.fetchingContextualData',
        guard: ({ event }) => event.contextualDataset != null,
        actions: [
          assign({
            contextualDataset: ({ event }) => event.contextualDataset,
            data: ({ context }) => ({
              ...context.data,
              contextualData: null,
            }),
          }),
          'markInteracted',
        ],
      },
      {
        actions: [
          assign({
            contextualDataset: ({ event }) => event.contextualDataset,
            data: ({ context }) => ({
              ...context.data,
              contextualData: null,
            }),
          }),
          'markInteracted',
        ],
      },
    ],
    CHANGE_TABLE_DATA_VIEW: {
      actions: [
        assign({
          tableDataView: ({ event }) => event.tableDataView,
        }),
        'markInteracted',
      ],
    },
  },
  states: {
    fetchingDatasets: {
      tags: ['loading'],
      invoke: {
        src: 'fetchDatasets',
        onDone: [{ target: 'fetchingOptions', actions: 'saveDatasets' }],
      },
    },
    fetchingOptions: {
      tags: ['loading'],
      invoke: {
        src: 'fetchOptions',
        input: ({ context }) => ({ dataset: context.dataset as string }),
        onDone: [{ target: 'fetchingData', actions: 'saveOptions' }],
      },
    },
    fetchingData: {
      tags: ['loading'],
      invoke: {
        src: 'fetchData',
        input: ({ context }) => ({
          context,
          dataset: context.dataset as string,
        }),
        onDone: [
          {
            target: 'fetchingContextualData',
            actions: 'saveData',
            guard: 'hasContextualDataset',
          },
          { target: 'check', actions: 'saveData' },
        ],
      },
    },
    fetchingContextualData: {
      tags: ['loading'],
      invoke: {
        src: 'fetchData',
        input: ({
          context: {
            category,
            categoryCompare,
            fundingSource,
            fundingSourceCompare,
            ...context
          },
        }) => {
          const level = context.level;
          const levelContextualDatasetLevelId =
            context.data.levelOptions?.[level]?.contextualDatasetLevelId;

          let levelCompare: Set<string> | undefined = undefined;
          if (
            levelContextualDatasetLevelId == null ||
            levelContextualDatasetLevelId === level
          ) {
            levelCompare = new Set();
            context.data.datasetData?.forEach((d) => {
              if (d.sublevelId && d.sublevelId !== DefaultFilterValues.level) {
                (levelCompare as Set<string>).add(d.sublevelId);
              }
            });
          }

          return {
            context: {
              ...context,
              category: DefaultFilterValues.category,
              categoryCompare: [],
              fundingSource: DefaultFilterValues.fundingSource,
              fundingSourceCompare: [],
              level: levelContextualDatasetLevelId ?? level,
              levelCompare:
                levelCompare != null
                  ? (Array.from(levelCompare).map((l) => [l]) as Compare)
                  : context.levelCompare,
            },
            dataset: context.contextualDataset as string,
          };
        },
        onDone: [{ target: 'check', actions: 'saveContextualData' }],
        onError: [
          { target: 'check', actions: assign({ contextualDataset: null }) },
        ],
      },
    },
    check: {
      tags: ['loading'],
      always: [
        { target: 'chart', guard: 'isChartView' },
        { target: 'map', guard: 'isMapView' },
        { target: 'table', guard: 'isTableView' },
      ],
    },
    chart: {
      initial: 'check',
      states: {
        check: {
          always: [
            {
              target: 'facetedMultipleLine',
              guard: 'isFacetedMultipleLine',
            },
            {
              target: 'rowFacetedStackedArea',
              guard: 'isRowFacetedStackedArea',
            },
            { target: 'facetedStackedArea', guard: 'isFacetedStackedArea' },
            { target: 'facetedStackedBar', guard: 'isFacetedStackedBar' },
            { target: 'multipleLine', guard: 'isMultipleLine' },
            { target: 'stackedArea', guard: 'isStackedArea' },
            {
              target: 'verticalStackedBar',
              guard: 'isVerticalStackedBar',
            },
            {
              target: 'horizontalStackedBar',
              guard: 'isHorizontalStackedBar',
            },
            { target: 'line', guard: 'isLine' },
            { target: 'area', guard: 'isArea' },
            { target: 'verticalBar', guard: 'isVerticalBar' },
            { target: 'horizontalBar', guard: 'isBar' },
          ],
        },
        horizontalStackedBar: withYearsStepperDefinition,
        verticalStackedBar: {},
        multipleLine: {},
        facetedStackedBar: {},
        facetedMultipleLine: {},
        stackedArea: {},
        facetedStackedArea: {},
        rowFacetedStackedArea: {},
        line: {},
        area: {},
        verticalBar: {},
        horizontalBar: withYearsStepperDefinition,
      },
    },
    table: {
      initial: 'check',
      states: {
        check: {
          always: [
            { target: 'threeDimensions', guard: 'isThreeDimensionsTable' },
            { target: 'twoDimensions', guard: 'isTwoDimensionsTable' },
            { target: 'oneDimension' },
          ],
        },
        oneDimension: {},
        twoDimensions: {},
        threeDimensions: {},
      },
    },
    map: withYearsStepperDefinition,
  },
});
